import { isPlatformBrowser, NgClass, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, inject, Input, PLATFORM_ID, ViewChild } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { Tab } from 'primeng/tabs';
import { Tooltip, TooltipModule } from 'primeng/tooltip';
import { IonSpinner } from '@ionic/angular/standalone';
import { UserService } from 'src/app/core/services/user.service';
import { PaTagComponent } from '../pa-tag/pa-tag.component';

@Component({
  selector: 'pa-tab',
  standalone: true,
  imports: [
    Tab,
    NgIf,
    NgClass,
    NgTemplateOutlet,
    IonSpinner,
    PaTagComponent,
    RouterLink,
    RouterLinkActive,
    TooltipModule,
  ],
  templateUrl: './pa-tab.component.html',
  styleUrls: ['./pa-tab.component.scss'],
})
export class PaTabComponent {
  /**
   * Значение вкладки
   */
  @Input() value: string | number;
  /**
   * Отключает переключение на эту вкладку
   */
  @Input() disabled: boolean = false;
  /**
   * Отображает бейдж с числом у вкладки
   */
  @Input() badge: number | null | undefined;
  /**
   * Отображает индикатор загрузки
   */
  @Input() loading: boolean = false;
  /**
   * Отображает бейджа профи у вкладки
   */
  @Input() isProfi: boolean = false;
  /**
   * Отображает бейдж нового элемента у вкладки
   */
  @Input() isNew: boolean = false;
  /**
   * Подсказка справа от названия вкладки
   */
  @Input() tooltip: string = null;
  /**
   * Делает вкладку ссылкой роутера
   */
  @Input() routerLink?: string | string[];

  @ViewChild(Tooltip) private readonly tabTooltip?: Tooltip;

  public readonly userService = inject(UserService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly canHover = this.resolveCanHover();
  private ignoreMouseLeaveUntil = 0;

  formatBadge(value: number | null | undefined): string {
    if (!value || value <= 0) {
      return '';
    }
    if (value > 99) {
      return '99+';
    }
    return value.toString();
  }

  shouldShowBadge(): boolean {
    return this.badge !== null && this.badge !== undefined && this.badge > 0 && !this.loading;
  }

  /**
   * Показывает только подсказку: не переключает вкладку и не уходит по routerLink.
   */
  handleTooltipInteraction(event: Event): void {
    event.stopPropagation();
    if (event.type !== 'click') {
      return;
    }
    event.preventDefault();
    this.ignoreMouseLeaveUntil = Date.now() + 500;
    this.showTooltip();
  }

  handleTooltipMouseEnter(): void {
    if (!this.canHover) {
      return;
    }
    this.showTooltip();
  }

  handleTooltipMouseLeave(): void {
    if (!this.canHover || Date.now() < this.ignoreMouseLeaveUntil) {
      return;
    }
    this.tabTooltip?.deactivate();
  }

  private showTooltip(): void {
    this.tabTooltip?.activate();
    this.fitTooltipInViewport();
  }

  /**
   * Ограничивает ширину и сдвигает подсказку, чтобы она не уезжала за край экрана.
   */
  private fitTooltipInViewport(): void {
    const tooltip = this.tabTooltip;
    const container = tooltip?.container as HTMLElement | undefined;
    if (!tooltip || !container) {
      return;
    }

    const gutter = 12;
    const maxWidth = Math.min(320, window.innerWidth - gutter * 2);
    container.style.maxWidth = `${maxWidth}px`;
    tooltip.alignBottom();

    const rect = container.getBoundingClientRect();
    let left = parseFloat(container.style.left) || 0;
    if (rect.left < gutter) {
      left += gutter - rect.left;
    } else if (rect.right > window.innerWidth - gutter) {
      left -= rect.right - (window.innerWidth - gutter);
    }
    container.style.left = `${left}px`;
  }

  private resolveCanHover(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return true;
    }
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }
}
